import React, { useState, useEffect } from 'react';
import '../OJTCoordinator/OJTCoordinator.css'; // Reuse the coordinator styling
import './SupervisorHome.css'; // Supervisor-specific styles
import { FaUser, FaUsers, FaClock } from 'react-icons/fa';
import { FaLocationDot, FaArrowRightFromBracket } from 'react-icons/fa6';
import { supabase } from '../../lib/supabaseClient';
import { useSupervisorAuth } from '../../lib/AuthProvider';

interface MyStudent {
  id: string;
  name: string;
  department: string;
  studentId: string;
  totalHours: number;
  progressRate: number;
  supervisor: string;
  trackingId: string;
  assignedDate: string;
}

interface AttendanceForm {
  traineeId: string;
  date: string;
  location: string;
  amTimeIn: string;
  amTimeOut: string;
  pmTimeIn: string;
  pmTimeOut: string;
}

const SupervisorHome: React.FC = () => {
  const [activeView, setActiveView] = useState<'students' | 'attendance'>('students');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [myStudents, setMyStudents] = useState<MyStudent[]>([]);
  const [supervisorOffice, setSupervisorOffice] = useState<string>('');
  const [attendanceForm, setAttendanceForm] = useState<AttendanceForm>({
    traineeId: '',
    date: '',
    location: '',
    amTimeIn: '',
    amTimeOut: '',
    pmTimeIn: '',
    pmTimeOut: ''
  });
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [locations, setLocations] = useState<Array<{id: string, name: string, latitude: number, longitude: number}>>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Use auth context to derive supervisor identity and loading state
  const { supervisor, loading: authLoading, signOut } = useSupervisorAuth();

  // When supervisor is available and not loading, fetch students based on office
  useEffect(() => {
    if (authLoading) return;
    if (supervisor?.office) {
      setSupervisorOffice(supervisor.office);
      fetchMyStudents(supervisor.office);
    }
  }, [authLoading, supervisor?.office]);

  // Fetch locations from the locations table
  const fetchLocations = async () => {
    setLocationsLoading(true);
    try {
      const { data: locationsData, error } = await supabase
        .from('locations')
        .select('id, name, latitude, longitude')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching locations:', error);
        return;
      }

      setLocations(locationsData || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLocationsLoading(false);
    }
  };

  // Fetch students where department matches supervisor's office
  const fetchMyStudents = async (supervisorOffice: string) => {
    try {
      // Get all students where department matches supervisor's office
      const { data: studentsData, error } = await supabase
        .from('student_profile')
        .select('*')
        .eq('department', supervisorOffice)
        .order('last_name', { ascending: true });

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      // Fetch daily hours for each student
      const studentsWithHours = await Promise.all(
        (studentsData || []).map(async (student: any) => {
          const { data: dailyHours } = await supabase
            .from('daily_hours')
            .select('total_hours')
            .eq('user_id', student.id);

          const totalHours = dailyHours?.reduce((sum: number, day: any) => sum + day.total_hours, 0) || 0;
          const progressRate = Math.round((totalHours / 500) * 100);

          return {
            id: student.id,
            name: `${student.first_name} ${student.last_name}`,
            department: student.department,
            studentId: student.student_id,
            totalHours: Math.round(totalHours * 100) / 100,
            progressRate: Math.min(progressRate, 100),
            supervisor: student.supervisor || '',
            trackingId: '', // Not needed anymore
            assignedDate: student.created_at || new Date().toISOString()
          };
        })
      );

      setMyStudents(studentsWithHours);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };


  // Handle attendance form submission
  const handleAttendanceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!attendanceForm.traineeId || !attendanceForm.date || !attendanceForm.location) {
      alert('Please fill in trainee, date, and location.');
      return;
    }

    // Check if at least one time is provided
    if (!attendanceForm.amTimeIn && !attendanceForm.amTimeOut && !attendanceForm.pmTimeIn && !attendanceForm.pmTimeOut) {
      alert('Please provide at least one time entry.');
      return;
    }

    setAttendanceLoading(true);
    try {
      // Get student details for the check-in record
      const selectedStudent = myStudents.find(student => student.id === attendanceForm.traineeId);
      if (!selectedStudent) {
        alert('Selected trainee not found.');
        return;
      }

      // Get selected location details
      const selectedLocation = locations.find(loc => loc.id === attendanceForm.location);
      if (!selectedLocation) {
        alert('Selected location not found.');
        return;
      }

      // Parse name correctly - take everything except the last word as first name
      // This handles names like "John Lloyd Micua" -> firstName: "John Lloyd", lastName: "Micua"
      const nameParts = selectedStudent.name.trim().split(' ');
      const firstName = nameParts.slice(0, -1).join(' ');
      const lastName = nameParts[nameParts.length - 1];

      // Type for supabase insert operation
      type SupabaseInsertOperation = {
        from: (table: string) => {
          insert: (data: Record<string, any> | Record<string, any>[]) => Promise<{error: any}>;
        };
      };

      const supabaseTyped = supabase as unknown as SupabaseInsertOperation;
      const checkInsToAdd = [];

      // Helper function to create UTC datetime from time input
      const convertToUTC = (time: string) => {
        const localDateTime = new Date(`${attendanceForm.date}T${time}:00`);
        return localDateTime.toISOString();
      };

      // Add AM Time In if provided
      if (attendanceForm.amTimeIn) {
        const timeInUTC = convertToUTC(attendanceForm.amTimeIn);
        checkInsToAdd.push({
          user_id: attendanceForm.traineeId,
          student_id: selectedStudent.studentId,
          first_name: firstName,
          last_name: lastName,
          location_id: attendanceForm.location,
          check_type: 'check_in_am',
          check_time: timeInUTC,
          check_date: attendanceForm.date,
          is_within_radius: true,
          coordinates: { 
            lat: selectedLocation.latitude, 
            lng: selectedLocation.longitude 
          }
        });
      }

      // Add AM Time Out if provided
      if (attendanceForm.amTimeOut) {
        const timeOutUTC = convertToUTC(attendanceForm.amTimeOut);
        checkInsToAdd.push({
          user_id: attendanceForm.traineeId,
          student_id: selectedStudent.studentId,
          first_name: firstName,
          last_name: lastName,
          location_id: attendanceForm.location,
          check_type: 'check_out_am',
          check_time: timeOutUTC,
          check_date: attendanceForm.date,
          is_within_radius: true,
          coordinates: { 
            lat: selectedLocation.latitude, 
            lng: selectedLocation.longitude 
          }
        });
      }

      // Add PM Time In if provided
      if (attendanceForm.pmTimeIn) {
        const timeInUTC = convertToUTC(attendanceForm.pmTimeIn);
        checkInsToAdd.push({
          user_id: attendanceForm.traineeId,
          student_id: selectedStudent.studentId,
          first_name: firstName,
          last_name: lastName,
          location_id: attendanceForm.location,
          check_type: 'check_in_pm',
          check_time: timeInUTC,
          check_date: attendanceForm.date,
          is_within_radius: true,
          coordinates: { 
            lat: selectedLocation.latitude, 
            lng: selectedLocation.longitude 
          }
        });
      }

      // Add PM Time Out if provided
      if (attendanceForm.pmTimeOut) {
        const timeOutUTC = convertToUTC(attendanceForm.pmTimeOut);
        checkInsToAdd.push({
          user_id: attendanceForm.traineeId,
          student_id: selectedStudent.studentId,
          first_name: firstName,
          last_name: lastName,
          location_id: attendanceForm.location,
          check_type: 'check_out_pm',
          check_time: timeOutUTC,
          check_date: attendanceForm.date,
          is_within_radius: true,
          coordinates: { 
            lat: selectedLocation.latitude, 
            lng: selectedLocation.longitude 
          }
        });
      }

      // Insert all check-ins
      const { error } = await supabaseTyped
        .from('check_ins')
        .insert(checkInsToAdd);

      if (error) {
        console.error('Error adding attendance:', error);
        alert('Error adding attendance. Please try again.');
        return;
      }

      // Reset form
      setAttendanceForm({
        traineeId: '',
        date: '',
        location: '',
        amTimeIn: '',
        amTimeOut: '',
        pmTimeIn: '',
        pmTimeOut: ''
      });

      const addedCount = checkInsToAdd.length;
      alert(`Attendance added successfully! (${addedCount} record${addedCount > 1 ? 's' : ''} added)`);
    } catch (error) {
      console.error('Error adding attendance:', error);
      alert('Error adding attendance. Please try again.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Handle form input changes
  const handleAttendanceFormChange = (field: keyof AttendanceForm, value: string) => {
    setAttendanceForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };

      // If trainee is selected, fetch locations
      if (field === 'traineeId' && value) {
        fetchLocations();
        // Reset location when trainee changes
        newForm.location = '';
      }

      return newForm;
    });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="coordinator-home">
      <header className="top-nav">
        <div className="nav-container">
          <div className="logo">
            <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
          </div>
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="supervisor-sidebar"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            {'\u2630'}
          </button>
          <div className="nav-links">
            <a 
              href="#" 
              className={`nav-link ${activeView === 'students' ? 'active' : ''}`}
              onClick={() => setActiveView('students')}
            >
              <FaUsers className="nav-icon" /> My Trainees
            </a>
            <a 
              href="#" 
              className={`nav-link ${activeView === 'attendance' ? 'active' : ''}`}
              onClick={() => setActiveView('attendance')}
            >
              <FaClock className="nav-icon" /> Attendance
            </a>
            <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
          </div>
          <button onClick={handleSignOut} className="nav-link logout-button"><FaArrowRightFromBracket className="nav-icon" /> Sign Out</button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="supervisor-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <a 
            href="#" 
            className="nav-link"
            onClick={() => setActiveView('students')}
          >
            <FaUsers className="nav-icon" /> My Trainees
          </a>
          <a 
            href="#" 
            className="nav-link"
            onClick={() => setActiveView('attendance')}
          >
            <FaClock className="nav-icon" /> Attendance
          </a>
          <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
          <button onClick={handleSignOut} className="nav-link logout-button"><FaArrowRightFromBracket className="nav-icon" /> Sign Out</button>
        </nav>
      </aside>

      <main className="main-content">
        {activeView === 'students' ? (
        <div className="students-section">
          <div className="students-header">
            <h3 className="coordinator-title">My Trainees</h3>
            {supervisorOffice && (
              <p className="office-info">Showing trainees from: <strong>{supervisorOffice}</strong></p>
            )}
          </div>

          {myStudents.length > 0 ? (
            <div className="trainee-table general-view">
              <table>
                <thead>
                  <tr>
                    <th>Trainee</th>
                    <th>Department</th>
                    <th>Total Hours</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myStudents.map((student, index) => (
                    <tr key={student.id}>
                      <td>{index + 1}.) {student.name}</td>
                      <td>{student.department}</td>
                      <td>
                        <div className="progress-container">
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${student.progressRate}%` }} 
                            />
                          </div>
                          <div className="progress-text">
                            <span>Progress Done: {student.progressRate}%</span>
                            <span>{student.totalHours}/500 hours</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="view-button">View</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data-message">
              <h3>No trainees found</h3>
              <p>There are currently no trainees in your department ({supervisorOffice}).</p>
              <p>Trainees will automatically appear here when their department matches your office.</p>
            </div>
          )}
        </div>
        ) : (
        <div className="attendance-section">
          <div className="attendance-header">
            <h3 className="coordinator-title">Add Attendance</h3>
            <p>Record AM and PM attendance for your trainees</p>
          </div>

          <div className="attendance-form-container">
            <form onSubmit={handleAttendanceSubmit} className="attendance-form">
              <div className="form-group">
                <label htmlFor="trainee">Trainee *</label>
                <select
                  id="trainee"
                  value={attendanceForm.traineeId}
                  onChange={(e) => handleAttendanceFormChange('traineeId', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Trainee</option>
                  {myStudents.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} ({student.studentId})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="date">Date *</label>
                <input
                  type="date"
                  id="date"
                  value={attendanceForm.date}
                  onChange={(e) => handleAttendanceFormChange('date', e.target.value)}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">Location *</label>
                <select
                  id="location"
                  value={attendanceForm.location}
                  onChange={(e) => handleAttendanceFormChange('location', e.target.value)}
                  className="form-select"
                  disabled={!attendanceForm.traineeId || locationsLoading}
                  required
                >
                  <option value="">
                    {!attendanceForm.traineeId 
                      ? "Select a trainee first" 
                      : locationsLoading 
                        ? "Loading locations..." 
                        : "Select Location"
                    }
                  </option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="time-section">
                <h4 className="time-section-title">AM</h4>
                <div className="time-fields-row">
                  <div className="form-group">
                    <label htmlFor="amTimeIn">Time In</label>
                    <input
                      type="time"
                      id="amTimeIn"
                      value={attendanceForm.amTimeIn}
                      onChange={(e) => handleAttendanceFormChange('amTimeIn', e.target.value)}
                      className="form-input"
                      min="00:00"
                      max="11:59"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="amTimeOut">Time Out</label>
                    <input
                      type="time"
                      id="amTimeOut"
                      value={attendanceForm.amTimeOut}
                      onChange={(e) => handleAttendanceFormChange('amTimeOut', e.target.value)}
                      className="form-input"
                      min="00:00"
                      max="12:59"
                    />
                  </div>
                </div>
              </div>

              <div className="time-section">
                <h4 className="time-section-title">PM</h4>
                <div className="time-fields-row">
                  <div className="form-group">
                    <label htmlFor="pmTimeIn">Time In</label>
                    <input
                      type="time"
                      id="pmTimeIn"
                      value={attendanceForm.pmTimeIn}
                      onChange={(e) => handleAttendanceFormChange('pmTimeIn', e.target.value)}
                      className="form-input"
                      min="12:00"
                      max="23:59"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="pmTimeOut">Time Out</label>
                    <input
                      type="time"
                      id="pmTimeOut"
                      value={attendanceForm.pmTimeOut}
                      onChange={(e) => handleAttendanceFormChange('pmTimeOut', e.target.value)}
                      className="form-input"
                      min="12:00"
                      max="17:59"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  disabled={attendanceLoading}
                  className="submit-button"
                >
                  {attendanceLoading ? 'Adding...' : 'Add Attendance'}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default SupervisorHome;
